import CustomButton from "../../../../components/CustomButton";
import SvgIcon, { SvgIconType } from "../../../../helpers/SvgIcon";

export interface ExpeditionSelectorOption<Value extends string> {
  value: Value;
  label: string;
  icon: SvgIconType;
}

interface Props<Value extends string> {
  label: string;
  value: Value;
  options: ExpeditionSelectorOption<Value>[];
  appearance: "cards" | "segmented";
  onChange: (value: Value) => void;
}

function ExpeditionSelector<Value extends string>({
  label,
  value,
  options,
  appearance,
  onChange,
}: Props<Value>) {
  const isCards = appearance === "cards";

  return (
    <div
      role="group"
      aria-label={label}
      className={`flex ${isCards ? "gap-3" : ""}`}>
      {options.map((option, index) => {
        const selected = option.value === value;
        const colors = selected
          ? isCards
            ? "!border-[#2196f3] !bg-[#f1f8ff] !text-[#2196f3] hover:!bg-[#e5f3ff]"
            : "!border-[#2196f3] !bg-[#2196f3] !text-white hover:!bg-[#1e88e5]"
          : isCards
          ? "!border-gray-200 !text-muted-dark hover:!border-[#2196f3]/50 hover:!bg-surface-subtle"
          : "!border-[#2196f3]/40 !text-[#2196f3] hover:!bg-[#f1f8ff]";
        const corners = isCards
          ? ""
          : index === 0
          ? "!rounded-r-none"
          : "-ml-px !rounded-l-none";

        return (
          <CustomButton
            key={option.value}
            type="button"
            variant="outlined"
            aria-pressed={selected}
            className={`!min-w-0 flex-1 !whitespace-nowrap !px-1 sm:!px-5 focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-[#2196f3] ${colors} ${corners}`}
            onClick={() => onChange(option.value)}>
            <span className="inline-flex items-center justify-center gap-2 sm:gap-3">
              <span
                aria-hidden="true"
                className="inline-flex h-[1.75em] w-[2.25em] shrink-0">
                <SvgIcon
                  type={option.icon}
                  fill="currentColor"
                  className="h-full w-full"
                  svgClassName="h-full w-full"
                />
              </span>
              <span>{option.label}</span>
            </span>
          </CustomButton>
        );
      })}
    </div>
  );
}

export default ExpeditionSelector;
